// "Draft & send a reply" for a captured inbound email (Email Triage Hub).
//
// Drives the email_reply_sends row (migration 0052): the email-reply worker
// drafts + sends; this component writes owner intent (create draft request →
// edit / re-prompt → confirm → Send) and polls the row for the result.
//
// N1 (owner-approved reversal): clicking Send opens a deliberate confirmation
// step — a real email is about to leave the owner's own Gmail. Only "Yes, send
// it" sets status='send_requested', the ONLY transition that authorizes the
// worker to actually send. Nothing goes out without that explicit confirm.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Send,
  Loader2,
  Pencil,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import {
  useSentReplies,
  sentReplyCount,
  shouldSoftDeleteOnClose,
} from '@/components/inbox/SentReplies';

export type ReplySendStatus =
  | 'draft_requested'
  | 'drafting'
  | 'draft_ready'
  | 'draft_error'
  | 'send_requested'
  | 'sending'
  | 'sent'
  | 'send_error';

const TERMINAL = new Set<ReplySendStatus>(['draft_ready', 'draft_error', 'sent', 'send_error']);

type Row = {
  id: string;
  status: ReplySendStatus;
  to_email: string | null;
  to_name: string | null;
  subject: string | null;
  body: string | null;
  engine: 'cli' | 'api' | null;
  draft_meta: { placeholders?: string[] } | null;
  error: string | null;
  updated_at: string | null;
};

const ROW_SELECT =
  'id, status, to_email, to_name, subject, body, engine, draft_meta, error, updated_at';

export function SendReplyButton({
  sourceEmailId,
  accountEmail,
  toEmail,
  toName,
  ownerId,
  compact = false,
}: {
  sourceEmailId: string;
  /** Which connected Gmail sends the reply (the inbox that received the mail). */
  accountEmail: string;
  toEmail: string | null;
  toName: string | null;
  ownerId: string;
  compact?: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [seededAt, setSeededAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [instruction, setInstruction] = useState('');

  const { data: row } = useQuery<Row | null>({
    queryKey: ['email_reply_send', rowId],
    enabled: open && rowId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_reply_sends')
        .select(ROW_SELECT)
        .eq('id', rowId)
        .maybeSingle();
      if (error) throw error;
      return (data as Row | null) ?? null;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL.has(status) ? false : 1200;
    },
  });

  useEffect(() => {
    if (!row || row.status !== 'draft_ready') return;
    if (row.updated_at === seededAt) return;
    setTo(row.to_email ?? toEmail ?? '');
    setSubject(row.subject ?? '');
    setBody(row.body ?? '');
    setSeededAt(row.updated_at ?? null);
    setEditing(false);
    setConfirming(false);
  }, [row, seededAt, toEmail]);

  // When a reply actually lands 'sent', refresh the per-email sent-replies
  // thread in the detail pane so it shows up immediately (and the card flips to
  // its "replied" state) without a manual refresh.
  useEffect(() => {
    if (row?.status === 'sent') {
      void qc.invalidateQueries({ queryKey: ['email_reply_sends', sourceEmailId] });
    }
  }, [row?.status, qc, sourceEmailId]);

  const createDraft = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('email_reply_sends')
        .insert({
          owner_id: ownerId,
          source_email_id: sourceEmailId,
          account_email: accountEmail,
          to_email: toEmail,
          to_name: toName,
          status: 'draft_requested',
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (id) => setRowId(id),
    onError: () => toast.error('Could not start the draft.'),
  });

  const revise = useMutation({
    mutationFn: async (nextInstruction: string | null) => {
      const patch: Record<string, unknown> = {
        instruction: nextInstruction,
        status: 'draft_requested',
        error: null,
      };
      const trimmedTo = to.trim();
      if (nextInstruction !== null && trimmedTo) patch.to_email = trimmedTo;
      const { error } = await supabase
        .from('email_reply_sends')
        .update(patch)
        .eq('id', rowId)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onSuccess: () => {
      setInstruction('');
      qc.invalidateQueries({ queryKey: ['email_reply_send', rowId] });
    },
    onError: () => toast.error('Could not request changes.'),
  });

  // Set true the instant a send is requested. The close handler consults THIS
  // (synchronous, local) rather than the React-Query-cached row.status (which
  // lags a poll) so a click-outside right after "Yes, send it" can never
  // soft-delete a row that's already been armed for sending — which would
  // strand a confirmed send (the worker skips deleted rows).
  const [submittedSend, setSubmittedSend] = useState(false);

  const send = useMutation({
    mutationFn: async () => {
      const trimmedTo = to.trim();
      if (!trimmedTo || !trimmedTo.includes('@')) throw new Error('no recipient');
      const { error } = await supabase
        .from('email_reply_sends')
        .update({
          to_email: trimmedTo,
          subject: subject.trim(),
          body,
          status: 'send_requested',
          instruction: null,
        })
        .eq('id', rowId)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onMutate: () => setSubmittedSend(true),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['email_reply_send', rowId] });
    },
    onError: (err) =>
      toast.error(
        err instanceof Error && err.message === 'no recipient'
          ? 'Add a valid recipient address.'
          : 'Could not send.',
      ),
  });

  function handleOpenChange(next: boolean): void {
    // Block close ONLY while the send mutation is literally in flight (a few ms)
    // — NOT after it resolves. The stranding concern (soft-deleting an armed
    // row) is handled by the `!submittedSend` guard on the abandon path below,
    // so the dialog stays freely closable once sent. (Bug fix: previously this
    // also blocked on `submittedSend`, which left the "Sent ✓ / Done" state
    // permanently un-closable.)
    if (!next && send.isPending) return;
    setOpen(next);
    if (next) {
      setRowId(null);
      setSeededAt(null);
      setEditing(false);
      setInstruction('');
      setConfirming(false);
      setSubmittedSend(false);
      createDraft.mutate();
    } else if (rowId && row && shouldSoftDeleteOnClose(row.status, submittedSend)) {
      // Abandon an un-sent draft so the history stays "sent only". The pure
      // guard (submittedSend + status allowlist) ensures a confirmed send is
      // never soft-deleted out from under the worker even if the cached
      // row.status still reads draft_ready.
      supabase
        .from('email_reply_sends')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', rowId)
        .eq('owner_id', ownerId)
        .then(undefined, () => {});
    }
  }

  const status = row?.status;
  const busy = status === 'draft_requested' || status === 'drafting';
  const sending = status === 'sending' || status === 'send_requested';
  const placeholders = row?.draft_meta?.placeholders ?? [];

  // Shared cache with the detail pane's SentRepliesSection (same query key) — a
  // prior sent reply relabels the trigger to "Reply again".
  const { data: priorReplies } = useSentReplies(sourceEmailId);
  const hasReplied = sentReplyCount(priorReplies) > 0;
  const label = hasReplied ? 'Reply again' : 'Draft & send reply';

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleOpenChange(true)}
        aria-label={`${label} to ${toName ?? toEmail ?? 'the sender'}`}
        title={`${label} to ${toName ?? toEmail ?? 'the sender'}`}
      >
        <Mail className="w-4 h-4" aria-hidden="true" />
        {!compact && <span className="ml-1.5">{label}</span>}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reply to {toName ?? toEmail ?? 'sender'}</DialogTitle>
            <DialogDescription>
              Your assistant drafts a reply in your voice — review, revise, then send. This goes out
              as a real email from <span className="font-medium">{accountEmail}</span>.
            </DialogDescription>
          </DialogHeader>

          {createDraft.isError && !rowId && (
            <ErrorPane message="Couldn’t start the draft. Please try again.">
              <Button size="sm" variant="outline" onClick={() => createDraft.mutate()}>
                <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Try again
              </Button>
            </ErrorPane>
          )}

          {!createDraft.isError && (busy || !status) && <Spinner label="Drafting your reply…" />}

          {status === 'draft_error' && (
            <ErrorPane message={`Couldn’t draft that reply. ${row?.error ?? ''}`}>
              <Button size="sm" variant="outline" onClick={() => revise.mutate(null)}>
                <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Try again
              </Button>
            </ErrorPane>
          )}

          {status === 'sent' && (
            <div className="py-8 space-y-2 text-center">
              <CheckCircle2 className="w-8 h-8 text-sage mx-auto" aria-hidden="true" />
              <p className="text-sm text-foreground">Sent to {row?.to_email}.</p>
              <Button size="sm" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </div>
          )}

          {status === 'send_error' && (
            <ErrorPane message={`Couldn’t confirm the send. ${row?.error ?? ''}`}>
              <p className="text-[11px] text-attention">
                This email may already have been delivered. Only retry if you’ve confirmed it didn’t
                arrive.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setConfirming(false);
                  qc.invalidateQueries({ queryKey: ['email_reply_send', rowId] });
                  // Return to the editor to let the owner re-send deliberately.
                  supabase
                    .from('email_reply_sends')
                    .update({ status: 'draft_ready' })
                    .eq('id', rowId)
                    .eq('owner_id', ownerId)
                    .then(
                      () => setSeededAt(null),
                      () => {},
                    );
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Back to draft
              </Button>
            </ErrorPane>
          )}

          {sending && <Spinner label="Sending your reply…" />}

          {/* ---- Confirmation step (the "actual email?" gate) ---- */}
          {status === 'draft_ready' && confirming && (
            <div className="space-y-4">
              <div className="neo-inset p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className="w-5 h-5 text-attention shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      Are you sure? This sends a real email.
                    </p>
                    <p className="text-[13px] text-muted-foreground">
                      It will be delivered immediately from your Gmail — the recipient sees it as
                      coming from you.
                    </p>
                  </div>
                </div>
                <dl className="text-[13px] space-y-1.5 pl-8">
                  <Detail label="From" value={accountEmail} />
                  <Detail label="To" value={to.trim() || '—'} />
                  <Detail label="Subject" value={subject.trim() || '(no subject)'} />
                </dl>
                {/* Re-show the exact body about to be sent — this confirm step is
                    the N1 gate, so the owner sees the full payload, not just headers. */}
                <div className="pl-8">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    Message
                  </p>
                  <pre className="neo-inset max-h-40 overflow-y-auto whitespace-pre-wrap break-words p-2 font-sans text-[13px] text-foreground">
                    {body.trim() || '(empty message)'}
                  </pre>
                </div>
                {placeholders.length > 0 && (
                  <p className="pl-8 text-[11px] text-attention">
                    ⚠ Unfilled placeholders remain: {placeholders.join(', ')} — these will send
                    literally unless you go back and fill them.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={send.isPending} onClick={() => send.mutate()}>
                  <Send className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Yes, send it
                </Button>
              </div>
            </div>
          )}

          {/* ---- Editor ---- */}
          {status === 'draft_ready' && !confirming && (
            <div className="space-y-3">
              <FieldRow label="To">
                <Input
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  disabled={!editing}
                  className="text-sm"
                />
              </FieldRow>
              <FieldRow label="Subject">
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={!editing}
                  className="text-sm"
                />
              </FieldRow>
              <FieldRow label="Body">
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={!editing}
                  rows={9}
                  className="text-sm resize-y"
                />
              </FieldRow>

              {placeholders.length > 0 && (
                <p className="text-[11px] text-attention">
                  Fill in before sending: {placeholders.join(', ')}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {row?.engine === 'cli' ? 'Drafted free via Claude Code.' : 'Drafted via Claude.'}
              </p>

              <div className="flex items-center gap-2 pt-1">
                <Input
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Ask AI to revise — e.g. “make it shorter”"
                  className="text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && instruction.trim()) revise.mutate(instruction.trim());
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!instruction.trim() || revise.isPending}
                  onClick={() => revise.mutate(instruction.trim())}
                >
                  <Sparkles className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Revise
                </Button>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing((v) => !v)}
                  aria-pressed={editing}
                >
                  <Pencil className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  {editing ? 'Done editing' : 'Edit manually'}
                </Button>
                <Button
                  size="sm"
                  disabled={!to.trim().includes('@')}
                  onClick={() => setConfirming(true)}
                >
                  <Send className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Send…
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Spinner({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function ErrorPane({
  message,
  children,
}: {
  message: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="py-6 space-y-3">
      <p className="flex items-start gap-2 text-sm text-destructive">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </p>
      {children}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <dt className="w-16 shrink-0 uppercase tracking-wide text-[10px] text-muted-foreground pt-0.5">
        {label}
      </dt>
      <dd className="text-foreground break-all">{value}</dd>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
